import React, { useEffect, useState } from 'react';
import apiClient from '../utils/api';
import { formatCurrency } from '../utils/formatters';

const Reports = () => {
    const [revenueTrend, setRevenueTrend] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [metricsRes, trendRes, statusRes] = await Promise.all([
                    apiClient.get('/dashboard/metrics'),
                    apiClient.get('/dashboard/revenue-trend'),
                    apiClient.get('/dashboard/payment-status'),
                ]);
                setMetrics(metricsRes.data);
                setRevenueTrend(trendRes.data);
                setPaymentStatus(statusRes.data);
            } catch (err) {
                console.error('Failed to load reports:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    if (loading) return <div className="p-8 text-gray-500">Loading reports...</div>;

    const maxRevenue = Math.max(...revenueTrend.map(r => r.revenue), 1);

    const statusColors = {
        paid: '#10b981',
        sent: '#3b82f6',
        draft: '#9ca3af',
        overdue: '#ef4444',
    };

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
                <p className="text-gray-500 mt-1">Financial overview — view only</p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(metrics?.total_revenue)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Outstanding</p>
                    <p className="text-2xl font-bold text-orange-500">{formatCurrency(metrics?.outstanding)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Paid This Month</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(metrics?.paid_this_month)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Overdue Count</p>
                    <p className="text-2xl font-bold text-red-500">{metrics?.overdue_count}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Trend */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend (Last 6 Months)</h2>
                    {revenueTrend.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">No revenue data yet</div>
                    ) : (
                        <div className="flex items-end space-x-3 h-40">
                            {revenueTrend.map((item) => (
                                <div key={item.month} className="flex flex-col items-center flex-1">
                                    <div
                                        className="w-full bg-blue-500 rounded-t-md hover:bg-blue-600 transition-colors"
                                        style={{ height: `${(item.revenue / maxRevenue) * 100}%`, minHeight: '4px' }}
                                        title={formatCurrency(item.revenue)}
                                    />
                                    <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">{item.month}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Payment Status Breakdown */}
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status Breakdown</h2>
                    {paymentStatus.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">No data yet</div>
                    ) : (
                        <div className="space-y-3">
                            {paymentStatus.map((item) => {
                                const total = paymentStatus.reduce((sum, s) => sum + s.amount, 0);
                                const pct = total > 0 ? (item.amount / total) * 100 : 0;
                                return (
                                    <div key={item.status}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="font-medium capitalize text-gray-700">{item.status}</span>
                                            <span className="text-gray-500">{formatCurrency(item.amount)} ({pct.toFixed(0)}%)</span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2">
                                            <div
                                                className="h-2 rounded-full"
                                                style={{
                                                    width: `${pct}%`,
                                                    backgroundColor: statusColors[item.status] || '#6b7280'
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Reports;
